// One set of words for an audience's sync state, wherever the drawer and the list show one.
//
// The stored status is a worker's vocabulary (`awaiting_pii`, `polling`, `binding_ttd`), written
// by batch-worker's audience materializer into `sync_state` and the revision journal, and the
// typed `SyncStatus` / `AudienceSyncState` unions do not name every value production writes
// (`uploading_pii` and `polling` are untargeted there). So this reads any string, and a value it
// does not know reads "Unknown state" — never the raw value.

export type AudienceSyncTarget = 'meta' | 'ttd';

const PROVIDER: Record<AudienceSyncTarget, string> = { meta: 'Meta', ttd: 'TTD' };

/**
 * The state in words, or `null` when there is no state at all (each caller has its own words for
 * that: "Not started", "—", "Definition saved"). `target` names the destination when the status
 * belongs to one leg, so an untargeted `polling` can say where.
 */
export function audienceSyncStateWords(
  status: string | null | undefined,
  target?: string | null,
): string | null {
  if (typeof status !== 'string' || status.trim() === '') return null;
  const at = target === 'meta' || target === 'ttd' ? PROVIDER[target] : null;
  switch (status) {
    case 'pending':
      return 'Waiting to start';
    case 'creating_at_platforms':
      return 'Creating at Meta and TTD';
    case 'awaiting_pii':
      return 'Preparing customer list';
    case 'syncing':
      return 'In progress';
    case 'uploading_pii':
      return at ? `Uploading to ${at}` : 'Uploading';
    case 'uploading_pii_meta':
      return 'Uploading to Meta';
    case 'uploading_pii_ttd':
      return 'Uploading to TTD';
    case 'polling':
      return at ? `Processing at ${at}` : 'Processing';
    case 'polling_meta':
      return 'Processing at Meta';
    case 'polling_ttd':
      return 'Processing at TTD';
    case 'binding_ttd':
      return 'Linking at TTD';
    case 'synced':
      return 'Synced';
    case 'failed':
      return 'Failed';
    default:
      return 'Unknown state';
  }
}
